/**
 * Riftgate Arena — the locale registry.
 *
 * Pure data plus pure functions: no I/O, no globals, no DOM. Everything that
 * needs to know "which languages exist and what are they called" reads this,
 * so adding a language is one entry here plus one `locales/<code>.json`.
 *
 * ── WHY OUR CODES ARE BCP-47 AND CORE'S ARE `xx_YY` ─────────────────────────
 * The template stores locales as `en_GB` / `de_DE` (they double as translation
 * filenames server-side). `Intl.PluralRules`, `Intl.NumberFormat`, `<html lang>`
 * and `Accept-Language` all want BCP-47 (`en`, `pt-BR`). Rather than convert at
 * a dozen call sites, the game speaks BCP-47 everywhere and converts ONCE at
 * the two boundaries that talk to core: `toCoreLocale()` when writing a
 * preference or fetching core's bundle, `fromCoreLocale()` when reading one.
 *
 * ── THE FIVE LANGUAGES CORE DOES NOT KNOW ───────────────────────────────────
 * `coreShipped: false` marks a language the TEMPLATE has no bundle for (ru, lv,
 * pt-BR, tr, ja as of this writing). The game's own UI strings are complete for
 * all eight regardless — but core's `errors`/`emails`/`currencies` namespace
 * will fall back to core's default locale for those five, and
 * `PATCH /me/preferences` will reject them, because
 * `core/i18n/i18n.constants.ts` (which we may not edit) does not list them.
 * See `runtime.ts` for how both cases degrade, and the phase report for the
 * merge-risk note.
 */

/** Every language the game's UI catalogue is complete for. Sorted by code. */
export const SUPPORTED_LOCALES = ['de', 'en', 'es', 'ja', 'lv', 'pt-BR', 'ru', 'tr'] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** The source language. Every fallback chain ends here before it ends at the key. */
export const DEFAULT_LOCALE: Locale = 'en';

export type TextDirection = 'ltr' | 'rtl';

export interface LocaleDescriptor {
  /** Our identifier, and the `locales/<code>.json` filename. */
  readonly code: Locale;
  /** BCP-47 tag handed to `Intl.*` and written into `<html lang>`. */
  readonly tag: string;
  /** The language's name IN that language — the only correct label for a switcher. */
  readonly native: string;
  /** English name, for admin surfaces and `aria-label`s in a known language. */
  readonly english: string;
  /** Writing direction. All eight are `ltr` today; see `directionForTag()`. */
  readonly dir: TextDirection;
  /** The template's identifier for the same language. */
  readonly coreLocale: string;
  /** Does `backend/src/core/i18n/locales/` actually contain that file today? */
  readonly coreShipped: boolean;
}

export const LOCALES: Readonly<Record<Locale, LocaleDescriptor>> = Object.freeze({
  de: {
    code: 'de',
    tag: 'de-DE',
    native: 'Deutsch',
    english: 'German',
    dir: 'ltr',
    coreLocale: 'de_DE',
    coreShipped: true,
  },
  en: {
    code: 'en',
    tag: 'en-GB',
    native: 'English',
    english: 'English',
    dir: 'ltr',
    coreLocale: 'en_GB',
    coreShipped: true,
  },
  es: {
    code: 'es',
    tag: 'es-ES',
    native: 'Español',
    english: 'Spanish',
    dir: 'ltr',
    coreLocale: 'es_ES',
    coreShipped: true,
  },
  ja: {
    code: 'ja',
    tag: 'ja-JP',
    native: '日本語',
    english: 'Japanese',
    dir: 'ltr',
    coreLocale: 'ja_JP',
    coreShipped: false,
  },
  lv: {
    code: 'lv',
    tag: 'lv-LV',
    native: 'Latviešu',
    english: 'Latvian',
    dir: 'ltr',
    coreLocale: 'lv_LV',
    coreShipped: false,
  },
  'pt-BR': {
    code: 'pt-BR',
    tag: 'pt-BR',
    native: 'Português (Brasil)',
    english: 'Portuguese (Brazil)',
    dir: 'ltr',
    coreLocale: 'pt_BR',
    coreShipped: false,
  },
  ru: {
    code: 'ru',
    tag: 'ru-RU',
    native: 'Русский',
    english: 'Russian',
    dir: 'ltr',
    coreLocale: 'ru_RU',
    coreShipped: false,
  },
  tr: {
    code: 'tr',
    tag: 'tr-TR',
    native: 'Türkçe',
    english: 'Turkish',
    dir: 'ltr',
    coreLocale: 'tr_TR',
    coreShipped: false,
  },
});

/**
 * Primary subtags that are written right-to-left.
 *
 * We ship none of them. This exists so `directionForTag()` is a lookup rather
 * than an assumption: the day an Arabic or Hebrew catalogue lands, the RUNTIME
 * half is already done — `dir` and `lang` go onto `<html>` from
 * `applyDocumentAttributes()`, direction is decided in exactly one place, and
 * `Intl` handles numerals and dates per locale.
 *
 * The LAYOUT half is not done, and nothing here can do it. An honest statement
 * of where we stand, so nobody reads `dir="rtl"` as "RTL works":
 *   - No RTL catalogue exists, so this is untested against real content.
 *   - Direction-sensitive CSS is written by the screen authors, not here. It
 *     survives RTL only if they reach for Tailwind's LOGICAL utilities
 *     (`ms-*`/`me-*`, `ps-*`/`pe-*`, `start-*`/`end-*`, `text-start`) rather
 *     than the physical `ml-*`/`mr-*`/`left-*`/`right-*`. Nothing enforces that
 *     today — there is no lint rule and no test for it.
 *   - The match canvas is Pixi, which knows nothing about `dir` at all; a
 *     lane-based board would need its own mirroring decision, and "mirror the
 *     board" is a GAMEPLAY question for the sim, not a CSS one.
 * So: adding an RTL language is a real piece of work, not a config flag. What
 * this file guarantees is that the work starts from a correct `dir`, not that
 * the layout is ready for it.
 */
const RTL_LANGUAGES: ReadonlySet<string> = new Set([
  'ar',
  'arc',
  'ckb',
  'dv',
  'fa',
  'he',
  'khw',
  'ks',
  'ps',
  'sd',
  'ur',
  'yi',
]);

/** Direction for ANY BCP-47 tag, shipped or not. */
export function directionForTag(tag: string): TextDirection {
  const primary = tag.trim().toLowerCase().replace(/_/g, '-').split('-')[0] ?? '';
  return RTL_LANGUAGES.has(primary) ? 'rtl' : 'ltr';
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LOCALES, value);
}

export function localeDescriptor(locale: Locale): LocaleDescriptor {
  return LOCALES[locale];
}

/** BCP-47 tag for `Intl.*` and `<html lang>`. */
export function localeTag(locale: Locale): string {
  return LOCALES[locale].tag;
}

export function localeDirection(locale: Locale): TextDirection {
  return LOCALES[locale].dir;
}

/** Our code -> the template's `xx_YY` identifier. */
export function toCoreLocale(locale: Locale): string {
  return LOCALES[locale].coreLocale;
}

/** The template's `xx_YY` identifier -> our code, or null when we do not ship it. */
export function fromCoreLocale(code: string | null | undefined): Locale | null {
  if (typeof code !== 'string' || code === '') return null;
  const wanted = code.trim().toLowerCase();
  for (const descriptor of Object.values(LOCALES)) {
    if (descriptor.coreLocale.toLowerCase() === wanted) return descriptor.code;
  }
  // Not one of core's identifiers — try it as a plain language tag, so an
  // account holding `en-GB` or `pt` still resolves instead of silently
  // dropping the player back to English.
  return negotiateLocale([code]);
}

/** Does the TEMPLATE have a bundle (and a `SUPPORTED_LOCALES` entry) for this? */
export function coreSupportsLocale(locale: Locale): boolean {
  return LOCALES[locale].coreShipped;
}

function normaliseTag(tag: string): string {
  return tag.trim().toLowerCase().replace(/_/g, '-');
}

/**
 * Pick the best locale we ship for a ranked list of preferences.
 *
 * Handles what `navigator.languages`, an `Accept-Language` header and a stored
 * preference all actually contain:
 *   - exact tag match, case-insensitive     `pt-br` -> pt-BR, `en-GB` -> en
 *   - our code as written                   `pt-BR` -> pt-BR
 *   - core identifiers                      `de_DE` -> de
 *   - language-only, or a region we do not
 *     ship                                  `pt`, `pt-PT` -> pt-BR
 *
 * Ranking is positional: the caller's order is the user's order. Returns null
 * rather than the default so the caller can tell "the user asked for something
 * we have" apart from "nothing matched" — one of those is worth persisting.
 */
export function negotiateLocale(preferences: readonly string[]): Locale | null {
  const wanted = preferences
    .map((entry) => normaliseTag(String(entry ?? '')))
    .filter((entry) => entry !== '' && entry !== '*');

  for (const candidate of wanted) {
    // 1. exact match on our code or our Intl tag.
    for (const descriptor of Object.values(LOCALES)) {
      if (
        normaliseTag(descriptor.code) === candidate ||
        normaliseTag(descriptor.tag) === candidate ||
        normaliseTag(descriptor.coreLocale) === candidate
      ) {
        return descriptor.code;
      }
    }
    // 2. same language, different (or missing) region.
    const primary = candidate.split('-')[0] ?? '';
    for (const descriptor of Object.values(LOCALES)) {
      if (normaliseTag(descriptor.code).split('-')[0] === primary) return descriptor.code;
    }
  }
  return null;
}
